/**
 * T-G6-fe-route (Phase-17, FR-G6) — Kategorien-Settings-Tab.
 *
 * Tabellen-CRUD für expense_categories: list + inline-edit (click-to-
 * edit on cells, save-on-blur), "+ Neue Kategorie"-Button, soft-delete
 * with archive-fallback when the row is in use.
 *
 * Backend routes from T501 (`apps/api/src/routes/expense-categories.ts`)
 * + T-G6-soft-delete (DELETE handler with 409 on in-use/system-default).
 *
 * Inline-create-combobox (T-G6-fe-inline) is a sibling component used
 * at the 5 mount-points (Beleg-Form, Detail-Page, Durcharbeitemodus,
 * Bulk-Assign, Inline-Item-Editor). This route is the canonical CRUD
 * surface.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { Button, GlassPanel, LoadingSpinner, EmptyState, PageHeader } from '../components/ui';
import {
  useCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
} from '../services/api/categories';
import { useToastHelpers } from '../components/ui/Toast';
import type { ExpenseCategoryDto } from '@armard/shared';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Route = createFileRoute('/settings_/categories' as any)({
  component: SettingsCategoriesPage,
});

function parseDeductiblePercent(raw: string): number | null {
  const normalized = raw.trim().replace(',', '.');
  if (!/^\d+(?:\.\d+)?$/.test(normalized)) return null;
  const value = Number(normalized);
  return Number.isFinite(value) && value >= 0 && value <= 100 ? value : null;
}

function SettingsCategoriesPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <SettingsCategoriesInner />
      </AppShell>
    </ProtectedRoute>
  );
}

function SettingsCategoriesInner() {
  const { data, isLoading } = useCategories({ includeArchived: true });
  const create = useCreateCategory();
  const deleteCat = useDeleteCategory();
  const toast = useToastHelpers();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newClass, setNewClass] = useState<'sammelposten' | 'abschreibung'>('sammelposten');
  const [newDeductible, setNewDeductible] = useState('100');
  const deductiblePercent = parseDeductiblePercent(newDeductible);
  const deductibleError =
    deductiblePercent === null ? 'Bitte eine Zahl zwischen 0 und 100 eingeben.' : null;

  async function submitCreate() {
    if (!newName.trim() || deductiblePercent === null) return;
    try {
      await create.mutateAsync({
        name: newName.trim(),
        accountingClass: newClass,
        topic: 'sonstiges',
        deductiblePercent,
        depreciationYears: newClass === 'abschreibung' ? 3 : null,
        // displayOrder is required by the Zod create-schema; nudges
        // the row to the bottom of the list by default.
        displayOrder: (data?.pagination.total ?? 0) + 1,
      });
      toast.success(`Kategorie "${newName.trim()}" angelegt`);
      setNewName('');
      setNewDeductible('100');
      setShowCreate(false);
    } catch (err) {
      toast.error(`Anlegen fehlgeschlagen: ${(err as Error).message}`);
    }
  }

  async function handleDelete(cat: ExpenseCategoryDto) {
    try {
      await deleteCat.mutateAsync(cat.id);
      toast.success(`Kategorie "${cat.name}" gelöscht`);
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.includes('in_use')) {
        toast.error(
          `Kategorie "${cat.name}" ist in Belegen referenziert — bitte stattdessen archivieren (Edit → Archivieren).`
        );
      } else if (msg.includes('system_default')) {
        toast.error(`Kategorie "${cat.name}" ist System-Default und kann nicht gelöscht werden.`);
      } else {
        toast.error(`Löschen fehlgeschlagen: ${msg}`);
      }
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Kategorien"
        subtitle={`${data?.pagination.total ?? 0} Kategorien — Sammelposten + Abschreibung`}
        actions={
          <Button onClick={() => setShowCreate((v) => !v)} data-testid="category-add-button">
            {showCreate ? 'Abbrechen' : '+ Neue Kategorie'}
          </Button>
        }
      />

      {showCreate && (
        <div
          className="mb-6 p-4 bg-white/5 rounded border border-white/10 space-y-3"
          data-testid="category-create-form"
        >
          <label className="block text-sm text-white/80">
            Name
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              data-testid="category-create-name"
              className="mt-1 w-full px-3 py-1.5 rounded bg-white/5 border border-white/10 text-white"
            />
          </label>
          <label className="block text-sm text-white/80">
            Klasse
            <select
              value={newClass}
              onChange={(e) => setNewClass(e.target.value as 'sammelposten' | 'abschreibung')}
              data-testid="category-create-class"
              className="mt-1 w-full px-3 py-1.5 rounded bg-white/5 border border-white/10 text-white"
            >
              <option value="sammelposten">Sammelposten</option>
              <option value="abschreibung">Abschreibung</option>
            </select>
          </label>
          <label className="block text-sm text-white/80">
            Abzugsfähig (%)
            <input
              type="text"
              inputMode="decimal"
              value={newDeductible}
              onChange={(e) => setNewDeductible(e.target.value)}
              aria-invalid={deductibleError !== null}
              aria-describedby="category-create-deductible-error"
              data-testid="category-create-deductible"
              className="mt-1 w-full px-3 py-1.5 rounded bg-white/5 border border-white/10 text-white"
            />
            {deductibleError && (
              <p
                id="category-create-deductible-error"
                role="alert"
                className="mt-1 text-sm text-red-300"
              >
                {deductibleError}
              </p>
            )}
          </label>
          <Button
            onClick={submitCreate}
            disabled={create.isPending || !newName.trim() || deductiblePercent === null}
            data-testid="category-create-submit"
          >
            {create.isPending ? 'Speichere…' : 'Anlegen'}
          </Button>
        </div>
      )}

      {isLoading && <LoadingSpinner />}

      {data && data.items.length === 0 && (
        <EmptyState
          title="Keine Kategorien"
          description="Neue Kategorien legen die Klassifizierung deiner Belege fest."
        />
      )}

      {data && data.items.length > 0 && (
        <GlassPanel
          variant="subtle"
          padding="none"
          className="overflow-hidden"
          data-testid="categories-list-surface"
        >
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10 text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-white/40 uppercase tracking-wider">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Klasse</th>
                  <th className="px-4 py-3 text-right">Abzug %</th>
                  <th className="px-4 py-3 text-right">AfA-Jahre</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {data.items.map((c) => (
                  <CategoryRow
                    key={c.id}
                    cat={c}
                    onDelete={() => handleDelete(c)}
                    isDeleting={deleteCat.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </GlassPanel>
      )}
    </div>
  );
}

function CategoryRow({
  cat,
  onDelete,
  isDeleting,
}: {
  cat: ExpenseCategoryDto;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const update = useUpdateCategory(cat.id);
  const toast = useToastHelpers();
  const [editing, setEditing] = useState<null | 'name' | 'deductible' | 'years'>(null);
  const [draft, setDraft] = useState('');
  const [editError, setEditError] = useState<string | null>(null);

  function startEdit(field: 'name' | 'deductible' | 'years') {
    setEditing(field);
    setEditError(null);
    setDraft(
      field === 'name'
        ? cat.name
        : field === 'deductible'
          ? cat.deductiblePercent
          : cat.depreciationYears != null
            ? String(cat.depreciationYears)
            : ''
    );
  }

  function changeDraft(value: string) {
    setDraft(value);
    if (editError) setEditError(null);
  }

  async function commit() {
    if (editing === null) return;
    if (editing === 'deductible' && parseDeductiblePercent(draft) === null) {
      setEditError('Bitte eine Zahl zwischen 0 und 100 eingeben.');
      return;
    }
    if (editing === 'years' && draft !== '' && (!/^\d+$/.test(draft) || Number(draft) < 1)) {
      setEditError('Bitte eine ganze Jahreszahl eingeben.');
      return;
    }
    try {
      if (editing === 'name' && draft.trim() !== cat.name) {
        await update.mutateAsync({ name: draft.trim(), version: cat.version });
      } else if (editing === 'deductible') {
        const n = parseDeductiblePercent(draft)!;
        if (String(n) !== cat.deductiblePercent) {
          await update.mutateAsync({ deductiblePercent: n, version: cat.version });
        }
      } else if (editing === 'years') {
        const n = draft === '' ? null : Number.parseInt(draft, 10);
        if (n !== cat.depreciationYears) {
          await update.mutateAsync({ depreciationYears: n, version: cat.version });
        }
      }
    } catch (err) {
      toast.error(`Speichern fehlgeschlagen: ${(err as Error).message}`);
    } finally {
      setEditing(null);
    }
  }

  return (
    <tr
      data-testid={`category-row-${cat.id}`}
      className={`hover:bg-white/5 transition-colors ${cat.isArchived ? 'opacity-50' : ''}`}
    >
      <td className="px-4 py-3 text-white">
        {editing === 'name' ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => changeDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') setEditing(null);
            }}
            data-testid={`category-row-${cat.id}-edit-name`}
            className="bg-white/10 border border-white/20 rounded px-2 py-0.5 text-sm text-white w-full"
          />
        ) : (
          <button
            type="button"
            onClick={() => startEdit('name')}
            data-testid={`category-row-${cat.id}-name`}
            className="text-left w-full"
          >
            {cat.name}
            {cat.isSeeded && <span className="ml-2 text-[10px] text-white/40">(System)</span>}
          </button>
        )}
      </td>
      <td className="px-4 py-3 text-white/70">{cat.accountingClass}</td>
      <td className="px-4 py-3 text-white/70 text-right tabular-nums">
        {editing === 'deductible' ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => changeDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') setEditing(null);
            }}
            data-testid={`category-row-${cat.id}-edit-deductible`}
            aria-invalid={editError !== null}
            aria-describedby={editError ? `category-${cat.id}-deductible-error` : undefined}
            className="bg-white/10 border border-white/20 rounded px-2 py-0.5 text-sm text-white w-20 text-right"
          />
        ) : (
          <button
            type="button"
            onClick={() => startEdit('deductible')}
            data-testid={`category-row-${cat.id}-deductible`}
          >
            {cat.deductiblePercent}
          </button>
        )}
        {editing === 'deductible' && editError && (
          <span
            id={`category-${cat.id}-deductible-error`}
            role="alert"
            className="block text-xs text-red-300"
          >
            {editError}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-white/70 text-right tabular-nums">
        {cat.accountingClass === 'abschreibung' ? (
          editing === 'years' ? (
            <>
              <input
                autoFocus
                value={draft}
                onChange={(e) => changeDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commit();
                  if (e.key === 'Escape') setEditing(null);
                }}
                data-testid={`category-row-${cat.id}-edit-years`}
                aria-invalid={editError !== null}
                aria-describedby={editError ? `category-${cat.id}-years-error` : undefined}
                className="bg-white/10 border border-white/20 rounded px-2 py-0.5 text-sm text-white w-16 text-right"
              />
              {editError && (
                <span
                  id={`category-${cat.id}-years-error`}
                  role="alert"
                  className="block text-xs text-red-300"
                >
                  {editError}
                </span>
              )}
            </>
          ) : (
            <button
              type="button"
              onClick={() => startEdit('years')}
              data-testid={`category-row-${cat.id}-years`}
            >
              {cat.depreciationYears ?? '—'}
            </button>
          )
        ) : (
          '—'
        )}
      </td>
      <td className="px-4 py-3 text-white/50 text-xs">{cat.isArchived ? 'archiviert' : 'aktiv'}</td>
      <td className="px-4 py-3 text-right">
        <button
          type="button"
          onClick={onDelete}
          disabled={isDeleting}
          data-testid={`category-row-${cat.id}-delete`}
          className="text-xs text-red-300 hover:text-red-200 disabled:opacity-30"
        >
          {isDeleting ? '…' : 'Löschen'}
        </button>
      </td>
    </tr>
  );
}
